import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { SuiButtonComponent } from '@sui/button';
import { SuiCalloutComponent } from '@sui/callout';
import {
  DialogCloseReason,
  SuiDialogBase,
  SuiDialogConfig,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
  SuiDialogHeaderSlot,
  SuiDialogSimpleComponent,
} from '@sui/dialog';

import { ApiError } from 'src/app/core/models/api-error.models';
import { UtilsService } from 'src/app/core/services/utils.service';

export interface ServerFaultDialogData {
  fault: ApiError;
}

/** A fault nobody is reading closes itself, as the banner card did (WAP-1965). */
const AUTO_CLOSE_MS = 5000;

/**
 * The server-fault (5xx) surface of the feedback subsystem
 * (`.claude/docs/error-presentation.md`): the message in a danger callout, the
 * normalised error as collapsible JSON with a copy button, Close. Opened by
 * ServerFaultService only.
 */
@Component({
  selector: 'app-server-fault-dialog',
  templateUrl: './server-fault-dialog.component.html',
  styleUrl: './server-fault-dialog.component.scss',
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiDialogContentSlot,
    SuiDialogFooterSlot,
    SuiButtonComponent,
    SuiCalloutComponent,
    FaIconComponent,
    TranslatePipe,
  ],
})
export class ServerFaultDialogComponent extends SuiDialogBase<
  ServerFaultDialogData,
  void
> {
  static dialogConfig: Partial<SuiDialogConfig> = { width: 560 };

  private readonly utils = inject(UtilsService);

  protected readonly fault: ApiError = this.dialogData.fault;
  /** A request that never reached a server (WAP-1979): the title names the connection, not the server. */
  protected readonly unreachable = this.fault.httpCode === 0;
  protected readonly detailsJson = ServerFaultDialogComponent.toJson(
    this.fault,
  );
  protected readonly expanded = signal(false);
  protected readonly copied = signal(false);

  private autoClose: ReturnType<typeof setTimeout> | null = setTimeout(
    () => this.dismiss(),
    AUTO_CLOSE_MS,
  );

  constructor() {
    super();
    inject(DestroyRef).onDestroy(() => this.cancelAutoClose());
  }

  /** Opening the details means the user is reading: the timer goes. */
  protected toggleDetails(): void {
    this.expanded.update((open) => !open);
    this.cancelAutoClose();
  }

  protected async copyDetails(): Promise<void> {
    await this.utils.copyToClipboard(this.detailsJson);
    this.copied.set(true);
  }

  protected dismiss(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }

  private cancelAutoClose(): void {
    if (this.autoClose !== null) {
      clearTimeout(this.autoClose);
      this.autoClose = null;
    }
  }

  /** The normalised error without its empty fields. */
  private static toJson(fault: ApiError): string {
    const entries = Object.entries({
      code: fault.code,
      httpCode: fault.httpCode,
      message: fault.message,
      module: fault.module,
      action: fault.action,
      requestId: fault.requestId,
      details: fault.details,
    }).filter(([, value]) => value !== undefined && value !== null);
    return JSON.stringify(Object.fromEntries(entries), null, 2);
  }
}
